import { useEffect, useState, forwardRef } from "react";
import styled, { css } from "styled-components";
import { getLocale } from "react-i18nify";
import { useSnackbar } from "notistack";

import font from "src/styles/font";
import { checkNotEmpty } from "src/common";

import {
  TextField,
  Grid,
  Autocomplete,
  createFilterOptions,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  IconButton,
  InputAdornment,
  Tooltip as MuiTooltip,
} from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import ErrorIcon from "@mui/icons-material/Error";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

import { searchProduct } from "src/api/product";
import { getCertificatesList } from "src/api/certificate";
import { createTISI, getTISIList } from "src/api/tisi";

import { formatFromElastic } from "src/common";
import { useAuth } from "src/hooks/useAuth";
import { UseUpload } from "src/context/UploadProductContext";
import { useDialog } from "src/context/DialogContext";

import Tooltip from "src/components/upload/Tooltip";
import { MainSwitch as Switch } from "src/components/core/Switch";
import TextEditor from "src/components/core/TextEditor";
import ProductCard from "src/components/product/ProductCard";
import Button from "src/components/core/Button";

import UnitPriceGrid from "./UnitPriceGrid";
import BrandDialog from "./BrandDialog";
import TISDialog from "src/modules/product/upload/modal/TISDialog";
import CerDialog from "src/modules/product/upload/modal/CerDialog";
import GenerateDetailDialog from "src/modules/product/upload/modal/GenerateDetail";

const SpanLabel = styled.div`
  font-size: ${font.size.md};
  color: var(--secondary-color);
  font-weight: 400;

  span {
    font-size: ${font.size.md};
    font-weight: 400;
    color: var(--primary-color);
  }

  ${({ subTitle }) =>
    subTitle &&
    css`
      margin-bottom: 16px;
    `}
`;

const WrapperTextfield = styled.div`
  /* width: ${({ half }) => (half ? "48.5%" : "100%")}; */
  width: 100%;

  ${({ divTool }) =>
    divTool &&
    css`
      display: flex;
      gap: 10px;
    `}

  ${({ textEditor }) =>
    textEditor &&
    css`
      margin-top: 16px;
    `}

  ${({ divPrice }) =>
    divPrice &&
    css`
      display: flex;
      gap: 6px;
      margin-top: 24px;
    `}

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
    // text-align: center;
    // background: #fff;
  }
  .TextField-without-border-radius-amount fieldset {
    border-radius: 16px;
    width: 100%;
    text-align: center;
    background: #fff;
  }
  .MuiAutocomplete-root {
    /* margin: ${({ item }) => (item ? "0" : "24px 0")}; */
    margin: 0;
  }
  .MuiOutlinedInput-input {
    background: #fff;
    /* border-radius: 24px; */
    padding: 16px;
  }
  .MuiInputBase-root-MuiOutlinedInput-root {
    border-radius: 16px;
    background: #fff;
  }
  .MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root {
    border-radius: 16px;
  }

  .MuiTextField-root {
    border-radius: 24px;
  }
  .MuiOutlinedInput-root {
    border-radius: 16px;
  }
  .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input {
    padding: 7.5px 0;
  }
`;

const WrapperButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  width: max-content;
`;

const WrapperFormLabel = styled.div`
  min-height: 56px;
  display: flex;
  align-items: center;
  /* margin: auto 0; */
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 75px;
  height: 75px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const RowProductCard = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const WrapperProductCard = styled.div`
  width: 200px !important;
  padding-bottom: 1px;
  position: relative;
  margin-bottom: 40px;
  margin: 10px 8px 30px 8px;
`;

const WarningText = styled(Box)`
  width: 100%;
  /* margin-top: 12px; */
  display: flex;
  gap: 8px;

  span {
    padding-top: 3px;
    font-size: ${font.size.xs};
    color: var(--darkgrey-color);
  }
`;

const WrapperTextfieldAuto = styled.div`
  /* width: ${({ half }) => (half ? "48.5%" : "100%")}; */
  width: 100%;

  ${({ divTool }) =>
    divTool &&
    css`
      display: flex;
      gap: 10px;
    `}

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
    // text-align: center;
    // background: #fff;
  }
  .TextField-without-border-radius-amount fieldset {
    border-radius: 16px;
    width: 100%;
    text-align: center;
    background: #fff;
  }
  .MuiAutocomplete-root {
    /* margin: ${({ item }) => (item ? "0" : "24px 0")}; */
    margin: 0;
  }
  .MuiOutlinedInput-input {
    background: #fff;
    border-radius: 16px;
    padding: 16px;
  }
  .MuiInputBase-root-MuiOutlinedInput-root {
    border-radius: 16px;
    background: #fff;
  }
  .MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root {
    border-radius: 16px;
  }

  .MuiTextField-root {
    border-radius: 16px;
  }
  .MuiOutlinedInput-root {
    border-radius: 16px;
    background: #fff;
  }
  .MuiAutocomplete-root .MuiOutlinedInput-root {
    padding-left: 16px;
  }
  .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input {
    padding: 7.5px 0;
  }
`;

const UploadedPreview = styled.div`
  /* container for multiple uploaded previews */
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 90px;
  padding: 10px;
  background: #f8f8f9;
  border-radius: 12px;
  border: 1px dashed #e6e6e6;
  box-sizing: border-box;
  overflow: auto;
`;

/* thumbnail image inside the container */
const PreviewImage = styled.img`
  width: 75px;
  height: 75px;
  object-fit: cover;
  display: block;
  border-radius: 8px;
  flex: 0 0 auto;
`;

const MainProduct = ({ customerType, onChange }) => {
  const { user } = useAuth();
  const {
    dataUpload,
    dataUploadChoice,
    similarData,
    dataSimilar,
    setDataSimilar,
    masterdata,
    onChangeUpload,
    uploadErrors,
    choiceErrors,
    initialConfirm,
    setConfirmInfo,
  } = UseUpload();
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [certificatesList, setCertificatesList] = useState([]);
  const [TISIList, setTISIList] = useState([]);
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [productList, setProductList] = useState([]);
  const [certificatePreviews, setCertificatePreviews] = useState([]); // [{ file, preview, isImage }]

  useEffect(() => {
    (async () => {
      try {
        const cer = await getCertificatesList();
        setCertificatesList(cer.items);
      } catch (error) {
        console.error("Error fetching certificates:", error);
      }
    })();
  }, []);
  useEffect(() => {
    setEditorLoaded(true);
  }, []);
  useEffect(() => {
    const sellerId = dataUpload?.user_id?.seller?._id || user._id;
    if (!sellerId) return;

    let mounted = true;
    (async () => {
      try {
        const tisi = await getTISIList(sellerId);
        if (mounted) setTISIList(tisi?.items || []);
      } catch (error) {
        console.error("Error fetching TISI:", error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpload, user._id]);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  const filter = createFilterOptions();

  const sortHashtag = masterdata.hashtag_data.sort((a, b) => {
    return b.product_count - a.product_count;
  });

  const onSubmitTISI = async (data) => {
    try {
      const formData = new FormData();
      formData.append("code", data.code);
      formData.append("url", data.url);
      formData.append("user_id", dataUpload.user_id.seller?._id || user._id);
      formData.append("image", data.image);
      formData.append("qrCode", data.qrCode);
      const createdtis = await createTISI(formData);
      if (createdtis) {
        enqueueSnackbar("เพิ่มมาตรฐาน TISI สำเร็จ", { variant: "success" });
        const tisi = await getTISIList(
          dataUpload.user_id.seller?._id || user._id,
        );
        setTISIList(tisi.items);
        closeAllDialogs();
      } else {
        enqueueSnackbar("เพิ่มมาตรฐาน TISI ไม่สำเร็จ", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("เพิ่มมาตรฐาน TISI ไม่สำเร็จ", { variant: "error" });
      console.error("Error creating TISI:", error);
    }
  };

  useEffect(() => {
    (async () => {
      await searchProduct({}).then(async ({ data }) => {
        setProductList(
          data?.rows.length > 0
            ? data?.rows?.map((product) => ({
                ...formatFromElastic(product),
              }))
            : [],
        );
      });
    })();
  }, []);

  useEffect(() => {
    const files = dataUpload?.certificate_files || [];
    const createdUrls = [];

    const previews = files.map((f) => {
      // server-provided doc (has docUrl)
      if (f && typeof f === "object" && f.docUrl) {
        return {
          file: { name: f.docUrl },
          preview: f.docUrl,
          isImage: f.docUrl ? !f.docUrl.toLowerCase().endsWith(".pdf") : false,
          originalName: f.originalFileName || "",
        };
      }

      // File (or Blob) instance
      if (
        f instanceof File ||
        (f && typeof f === "object" && ("size" in f || "type" in f))
      ) {
        try {
          const url = URL.createObjectURL(f);
          createdUrls.push(url);
          return {
            file: f,
            preview: url,
            isImage: f.type ? f.type.startsWith("image/") : false,
            originalName: f.name || "",
          };
        } catch (e) {
          return {
            file: f,
            preview: null,
            isImage: false,
            originalName: f.name || "",
          };
        }
      }

      // fallback for unknown shape
      return {
        file: f,
        preview: null,
        isImage: false,
        originalName: f?.originalFileName || "",
      };
    });

    setCertificatePreviews((prev) => {
      const same =
        prev.length === previews.length &&
        prev.every(
          (p, i) =>
            p.preview === previews[i].preview &&
            p.originalName === previews[i].originalName &&
            (p.file?.name || "") === (previews[i].file?.name || ""),
        );
      return same ? prev : previews;
    });

    return () => {
      createdUrls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (e) {
          /* ignore */
        }
      });
    };
  }, [dataUpload?.certificate_files]);

  // Remove certificate file and preview by index
  const removeCertificateFile = (indexToRemove) => {
    setCertificatePreviews((prev) =>
      prev.filter((_, i) => i !== indexToRemove),
    );
    if (Array.isArray(dataUpload?.certificate_files)) {
      const newFiles = dataUpload.certificate_files.filter(
        (_, i) => i !== indexToRemove,
      );
      onChangeUpload(null, "certificate_files", newFiles);
    }
  };

  const AiGenerateButton = () => {
    return (
      <MuiTooltip
        title="การกรอกข้อมูลเบื้องต้นให้ครบ เช่น หมวดหมู่ ประเภทสินค้า สี ลวดลาย จะช่วยให้ AI คิดคำได้เหมาะสมยิ่งขึ้น"
        placement="top"
      >
        <Button
          variant="ai-suggest"
          size="small"
          startIcon={<AutoAwesomeOutlinedIcon />}
          onClick={() =>
            openDialog({
              type: "custom",
              customDialog: (
                <GenerateDetailDialog
                  dataUpload={dataUpload}
                  dataUploadChoice={dataUploadChoice}
                  handleChange={onChangeUpload}
                  onSuccess={() => closeAllDialogs()}
                  onClose={() => closeDialog()}
                />
              ),
            })
          }
        >
          AI ช่วยคิด
        </Button>
      </MuiTooltip>
    );
  };

  return (
    <>
      <Grid
        container
        rowSpacing={3}
        sx={{
          m: 0,
          p: 5,
          width: "100%",
          borderRadius: "24px",
          background: "var(--white-color)",
          boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.10)",
          "&>.MuiGrid-item:first-child": {
            pt: 0,
          },
        }}
      >
        {/* Company */}
        <Grid item xs={12}>
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            columnSpacing={3}
            sx={{
              mt: 0,
              ".MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-20px",
              },
            }}
          >
            {user?.role_id === 1 ? (
              <Grid item xs={6}>
                <SpanLabel subTitle>
                  บริษัท<span>*</span>
                </SpanLabel>

                <WrapperTextfield className="form-input" divTool>
                  <Autocomplete
                    id="search-seller-user"
                    isOptionEqualToValue={(option, value) =>
                      option?.seller?._id === value?.seller?._id
                    }
                    renderOption={(props, option) => (
                      <li {...props}>{option?.seller?.company_name}</li>
                    )}
                    options={masterdata?.seller_data}
                    value={dataUpload?.user_id}
                    onChange={(e, newValue) => {
                      onChangeUpload(null, `user_id`, newValue);
                    }}
                    getOptionLabel={(option) => {
                      return option?.seller?.company_name;
                    }}
                    // filterOptions={filterOptions}
                    sx={{
                      my: 2,
                      width: "100%",
                      background: "#fff",
                      ".MuiOutlinedInput-root": {
                        paddingLeft: "16px",
                      },
                      ".MuiAutocomplete-option": {
                        p: 3,
                      },
                    }}
                    disabled={dataUpload._id !== null}
                    renderInput={(params) => (
                      <TextField
                        className="TextField-without-border-radius"
                        {...params}
                        label="บริษัท"
                        disabled={dataUpload._id !== null}
                        error={!!uploadErrors[`user_id`]}
                        helperText={
                          !!uploadErrors[`user_id`] ? "โปรดเลือกบริษัท" : ""
                        }
                      />
                    )}
                  />
                  <Tooltip title={"ชื่อบริษัท"} />
                </WrapperTextfield>
              </Grid>
            ) : null}

            <Grid item xs={6}>
              <SpanLabel subTitle>
                แบรนด์<span>*</span>
              </SpanLabel>

              <WrapperTextfield className="form-input" divTool>
                <Autocomplete
                  id="search-brands"
                  // isOptionEqualToValue={(option, value) =>
                  //   option?._id === value?._id
                  // }
                  renderOption={({ key, ...optionProps }, option) => (
                    <li key={key} {...optionProps}>
                      {option.name}
                    </li>
                  )}
                  options={masterdata?.brand_data}
                  value={dataUpload?.brand}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      // timeout to avoid instant validation of the dialog's form.
                      setTimeout(() => {
                        openDialog({
                          type: "custom",
                          customDialog: (
                            <BrandDialog
                              value={{
                                name: newValue,
                              }}
                              isOpen={true}
                              onSubmit={onChangeUpload}
                              onClose={() => closeDialog()}
                            />
                          ),
                        });
                      });
                    } else if (newValue && newValue.inputValue) {
                      openDialog({
                        type: "custom",
                        customDialog: (
                          <BrandDialog
                            value={{
                              name: newValue.inputValue,
                            }}
                            isOpen={true}
                            onSubmit={onChangeUpload}
                            onClose={() => closeDialog()}
                          />
                        ),
                      });
                    } else {
                      onChangeUpload(null, `brand`, newValue);
                    }
                  }}
                  getOptionLabel={(option) => {
                    // e.g value selected with enter, right from the input
                    if (typeof option === "string") {
                      return option;
                    }
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    return option?.name;
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (params.inputValue !== "") {
                      filtered.push({
                        inputValue: params.inputValue,
                        name: `Add "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  freeSolo
                  sx={{
                    my: 2,
                    width: "100%",
                    background: "#fff",
                    ".MuiOutlinedInput-root": {
                      paddingLeft: "16px",
                    },
                    ".MuiAutocomplete-option": {
                      p: 3,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      className="TextField-without-border-radius"
                      {...params}
                      label="แบรนด์"
                      error={!!uploadErrors[`brand`]}
                      helperText={
                        !!uploadErrors[`brand`] ? "โปรดเลือกแบรนด์" : ""
                      }
                    />
                  )}
                />
                <Tooltip title={"โปรดระบุแบรนด์ หรือชื่อยี่ห้อสินค้าของคุณ"} />
              </WrapperTextfield>
            </Grid>

            {user?.role_id === 3 ? (
              <Grid item xs={6}>
                <WrapperTextfield className="form-input">
                  <div
                    style={{
                      width: "100%",
                      background: "#fff",
                      boxSizing: "border-box",
                    }}
                  >
                    <a
                      href="/tutorial"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-block",
                        width: "100%",
                        paddingRight: "34px",
                      }}
                    >
                      <img
                        src="https://xsurface-images.s3.ap-southeast-1.amazonaws.com/%E0%B8%9B%E0%B8%81%E0%B9%80%E0%B8%A7%E0%B8%9A_.png"
                        alt="tutorial"
                        style={{
                          width: "100%",
                          height: "auto",
                          cursor: "pointer",
                          display: "block",
                          borderRadius: "16px",
                        }}
                      />
                    </a>
                  </div>
                </WrapperTextfield>
              </Grid>
            ) : null}
          </Grid>
        </Grid>

        {/* Product Detail */}
        <Grid item xs={12} sx={{ "&.MuiGrid-item": { pt: 5 } }}>
          <SpanLabel>
            รายละเอียดสินค้า<span>*</span>
          </SpanLabel>

          <Grid
            container
            rowSpacing={0}
            columnSpacing={3}
            sx={{
              mt: 0,
              ".MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-20px",
              },
            }}
          >
            {/* left */}
            <Grid item xs={6}>
              <Grid
                container
                spacing={0}
                rowSpacing={3}
                columnSpacing={0}
                sx={{
                  m: 0,
                  ".MuiFormHelperText-root": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
              >
                {dataUpload.is_choice && dataUploadChoice.length > 1 ? (
                  <Grid item xs={12}>
                    <WrapperTextfield className="form-input" divTool>
                      <TextField
                        label="ชื่อตัวเลือกสินค้าที่ 1/10"
                        variant="outlined"
                        fullWidth
                        value={dataUploadChoice[0]?.name}
                        onChange={(e) => onChangeUpload(e, `choices.0.name`)}
                        error={!!choiceErrors[`choices[0].name`]}
                        helperText={
                          !!choiceErrors[`choices[0].name`]
                            ? "โปรดระบุชื่อตัวเลือกสินค้า"
                            : ""
                        }
                      />
                      <Tooltip
                        title={
                          "โปรดใส่ชื่อตัวเลือกสินค้า เช่น สีขาว a1000 หรือ สีโอ๊คไวท์"
                        }
                      />
                    </WrapperTextfield>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <TextField
                      label="ชื่อสินค้า"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <AiGenerateButton />
                          </InputAdornment>
                        ),
                      }}
                      value={dataUpload?.item_name ? dataUpload?.item_name : ""}
                      onChange={(e) => onChangeUpload(e, `item_name`)}
                      error={!!uploadErrors[`item_name`]}
                      helperText={
                        !!uploadErrors[`item_name`] ? "โปรดระบุชื่อสินค้า" : ""
                      }
                      InputLabelProps={{
                        shrink: !!dataUpload?.item_name,
                      }}
                    />
                    <Tooltip title={"โปรดระบุชื่อของสินค้า"} />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-category"
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.category_data}
                      value={dataUpload?.category}
                      onChange={(e, newValue) => {
                        onChangeUpload(null, `category`, newValue);
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.cate_th
                          : option.cate_en;
                      }}
                      disablePortal
                      blurOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="หมวดหมู่"
                          error={!!uploadErrors[`category`]}
                          helperText={
                            !!uploadErrors[`category`]
                              ? "โปรดเลือกหมวดหมู่สินค้า"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "ชื่อหมวดหมู่สินค้าที่ต้องการให้ปรากฏบนหน้าเว็บไซต์"
                      }
                    />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-color"
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.color_data}
                      value={dataUploadChoice[0]?.color}
                      onChange={(event, newValue) => {
                        onChangeUpload(null, `choices.0.color`, newValue);
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.color_th
                          : option.color_en;
                      }}
                      disablePortal
                      blurOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="สี"
                          error={!!choiceErrors[`choices[0].color`]}
                          helperText={
                            !!choiceErrors[`choices[0].color`]
                              ? "โปรดเลือกสี"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "โปรดระบุสีที่ตรงกับสินค้า หรือใกล้เคียงกับสินค้าของคุณ"
                      }
                    />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-texture"
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.textureType_data}
                      value={dataUploadChoice[0]?.texture_type}
                      onChange={(event, newValue) => {
                        onChangeUpload(
                          null,
                          `choices.0.texture_type`,
                          newValue,
                        );
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.type_th
                          : option.type_en;
                      }}
                      disablePortal
                      blurOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="ผิวสัมผัส"
                          error={!!choiceErrors[`choices[0].texture_type`]}
                          helperText={
                            !!choiceErrors[`choices[0].texture_type`]
                              ? "โปรดเลือกผิวสัมผัส"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "โปรดระบุผิวสัมผัสที่ตรงกับสินค้า หรือใกล้เคียงกับสินค้าของคุณ"
                      }
                    />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-suitable-areas"
                      multiple
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.suitableArea_data}
                      value={dataUpload?.suitable_areas}
                      onChange={(event, newValue) => {
                        onChangeUpload(null, `suitable_areas`, newValue);
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.area_th
                          : option.area_en;
                      }}
                      disableCloseOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="พื้นที่ในการใช้งาน"
                          error={!!uploadErrors[`suitable_areas`]}
                          helperText={
                            !!uploadErrors[`suitable_areas`]
                              ? "โปรดเลือกพื้นที่การใช้งาน"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "โปรดระบุพื้นที่การใช้งานที่เหมาะสมกับสินค้าของคุณ โดยที่สามารถเลือกได้มากกว่า 1 ข้อ"
                      }
                    />
                  </WrapperTextfield>
                </Grid>
              </Grid>
            </Grid>

            {/* right */}
            <Grid item xs={6}>
              <Grid
                container
                spacing={0}
                rowSpacing={3}
                columnSpacing={0}
                sx={{
                  m: 0,
                  ".MuiFormHelperText-root": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
              >
                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <TextField
                      label="รหัสสินค้า"
                      variant="outlined"
                      fullWidth
                      value={dataUpload?.item_code}
                      onChange={(e) => onChangeUpload(e, `item_code`)}
                      disabled={checkNotEmpty(dataUpload?._id)}
                      error={!!uploadErrors[`item_code`]}
                      helperText={
                        !!uploadErrors[`item_code`] ? "โปรดระบุรหัสสินค้า" : ""
                      }
                    />
                    <Tooltip title={"โปรดระบุรหัสของสินค้า"} />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-product-type"
                      multiple
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.productType_data}
                      value={dataUpload?.product_types}
                      onChange={(e, newValue) => {
                        onChangeUpload(null, `product_types`, newValue);
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.type_th
                          : option.type_en;
                      }}
                      // freeSolo
                      getOptionDisabled={(options) =>
                        dataUpload?.product_types?.length > 4 ? true : false
                      }
                      disableCloseOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="ประเภทสินค้า"
                          error={!!uploadErrors[`product_types`]}
                          helperText={
                            !!uploadErrors[`product_types`]
                              ? "โปรดเลือกประเภทสินค้า"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "กรุณาเลือกชื่อประเภทสินค้าที่ต้องการให้ปรากฏบนหน้าเว็บไซต์ สามารถเลือกได้สูงสุด 5 ตัว"
                      }
                    />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-pattern"
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.pattern_data}
                      value={dataUploadChoice[0]?.pattern}
                      onChange={(e, newValue) => {
                        onChangeUpload(null, `choices.0.pattern`, newValue);
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.pattern_th
                          : option.pattern_en;
                      }}
                      disablePortal
                      blurOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="ลวดลาย"
                          error={!!choiceErrors[`choices[0].pattern`]}
                          helperText={
                            !!choiceErrors[`choices[0].pattern`]
                              ? "โปรดเลือกลวดลาย"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "โปรดระบุ ลวดลายที่ตรง หรือใกล้เคียงกับสินค้าของคุณ"
                      }
                    />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-recommend-room"
                      multiple
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.recommendRoom_data || []} // ! delete []
                      value={dataUpload?.recommend_rooms}
                      onChange={(e, newValue) => {
                        onChangeUpload(null, `recommend_rooms`, newValue);
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.room_th
                          : // : option.room_en;
                            option.room_th;
                      }}
                      disableCloseOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="สถานที่ใช้งาน"
                          error={!!uploadErrors[`recommend_rooms`]}
                          helperText={
                            !!uploadErrors[`recommend_rooms`]
                              ? "โปรดเลือกสถานที่ใช้งาน"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "โปรดระบุสถานที่ใช้งานสินค้าของคุณ โดยที่สามารถเลือกได้มากกว่า 1 ข้อ"
                      }
                    />
                  </WrapperTextfield>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfield className="form-input" divTool>
                    <Autocomplete
                      id="select-product-feature"
                      multiple
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={masterdata?.productFeature_data || []} // ! delete []
                      value={dataUpload?.product_features}
                      onChange={(e, newValue) => {
                        onChangeUpload(null, `product_features`, newValue);
                      }}
                      getOptionLabel={(option) => {
                        return getLocale() === "th"
                          ? option.feature_th
                          : // : option.feature_en;
                            option.feature_th;
                      }}
                      disableCloseOnSelect
                      sx={{
                        my: 2,
                        width: "100%",
                        background: "#fff",
                        ".MuiOutlinedInput-root": {
                          paddingLeft: "16px",
                        },
                        ".MuiAutocomplete-option": {
                          p: 3,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          label="คุณสมบัติเฉพาะ"
                          error={!!uploadErrors[`product_features`]}
                          helperText={
                            !!uploadErrors[`product_features`]
                              ? "โปรดเลือกคุณสมบัติเฉพาะของสินค้า"
                              : ""
                          }
                        />
                      )}
                    />
                    <Tooltip
                      title={
                        "โปรดระบุคุณสมบัติเฉพาะของสินค้าของคุณ โดยที่สามารถเลือกได้มากกว่า 1 ข้อ"
                      }
                    />
                  </WrapperTextfield>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* TIS มอก */}
        {dataUpload?.user_id && (
          <Grid
            item
            xs={12}
            container
            spacing={2}
            sx={{ "&.MuiGrid-item": { pt: 5 } }}
          >
            <Grid item xs={12}>
              <WrapperButton>
                <FormControlLabel
                  label="สินค้ามี มอก."
                  sx={{
                    ".MuiFormControlLabel-label": {
                      ml: 1,
                      fontSize: font.size.md,
                    },
                  }}
                  control={
                    <Switch
                      sx={{ m: 1 }}
                      checked={
                        dataUpload.product_tisi &&
                        dataUpload.product_tisi !== null &&
                        dataUpload.product_tisi !== undefined
                      }
                      onChange={(e) => {
                        onChangeUpload(null, `product_tisi`, e.target.checked);
                      }}
                    />
                  }
                />
              </WrapperButton>
            </Grid>

            {dataUpload?.product_tisi && (
              <>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SpanLabel>รายละเอียด มอก.</SpanLabel>

                  <Button
                    variant="contained"
                    className="Button-Add"
                    sx={{
                      width: "100%",
                      maxWidth: 200,
                      fontSize: font.size.sm,
                    }}
                    startIcon={<AddIcon />}
                    onClick={() =>
                      openDialog({
                        type: "custom",
                        customDialog: (
                          <TISDialog
                            isOpen={true}
                            onClose={() => closeDialog()}
                            onSubmit={onSubmitTISI}
                          />
                        ),
                      })
                    }
                  >
                    เพิ่ม มอก. ใหม่
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <WrapperTextfieldAuto className="form-input">
                    <Autocomplete
                      disablePortal
                      blurOnSelect
                      id="select-tisi"
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={TISIList || []} // ! delete []
                      sx={{
                        ".MuiOutlinedInput-input": {
                          borderRadius: 0,
                        },
                      }}
                      value={dataUpload?.product_tisi || null}
                      onChange={(e, newValue) => {
                        onChangeUpload(null, `product_tisi`, newValue);
                      }}
                      getOptionLabel={(option) =>
                        option.code ? option.code : ""
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="เลือก มอก."
                          // error={
                          //   !!choiceErrors[
                          //     `choices[${choiceIndex}].group_price.unit_price`
                          //   ]
                          // }
                          // helperText={
                          //   !!choiceErrors[
                          //     `choices[${choiceIndex}].group_price.unit_price`
                          //   ]
                          //     ? "โปรดเลือกหน่วยราคา"
                          //     : ""
                          // }
                        />
                      )}
                    />
                  </WrapperTextfieldAuto>
                </Grid>
              </>
            )}
          </Grid>
        )}

        {/* Certificate ใบรับรอง */}
        <Grid
          item
          xs={12}
          container
          spacing={2}
          sx={{ "&.MuiGrid-item": { pt: 5 } }}
        >
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <SpanLabel>รายละเอียดใบรับรองทั้งหมด</SpanLabel>

            <Button
              variant="contained"
              className="Button-Add"
              sx={{
                width: "100%",
                maxWidth: 200,
                fontSize: font.size.sm,
              }}
              startIcon={<AddIcon />}
              onClick={() =>
                openDialog({
                  type: "custom",
                  customDialog: (
                    <CerDialog isOpen={true} onClose={() => closeDialog()} />
                  ),
                })
              }
            >
              เพิ่มเอกสารใบรับรอง
            </Button>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextfield className="form-input" divTool>
              <Autocomplete
                id="select-product-certificates"
                multiple
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id
                }
                options={certificatesList || []} // ! delete []
                value={dataUpload?.product_certificates}
                onChange={(e, newValue) => {
                  onChangeUpload(null, `product_certificates`, newValue);
                }}
                getOptionLabel={(option) => option.name}
                disableCloseOnSelect
                sx={{
                  my: 2,
                  width: "100%",
                  background: "#fff",
                  ".MuiOutlinedInput-root": {
                    paddingLeft: "16px",
                  },
                  ".MuiAutocomplete-option": {
                    p: 3,
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    className="TextField-without-border-radius"
                    {...params}
                    label="ใบรับรอง (กรณีไม่พบรายการ กรุณาติดต่อผู้ดูแลระบบเพื่อขอเพิ่มข้อมูล)"
                    error={!!uploadErrors[`certificates`]}
                  />
                )}
              />
            </WrapperTextfield>
          </Grid>

          {certificatePreviews.length > 0 && (
            <Grid item xs={12}>
              <UploadedPreview>
                {certificatePreviews.length > 0 ? (
                  certificatePreviews.map((p, index) => (
                    <Box
                      key={`${p.file.name}_${p.file.size || "-"}_${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                        minWidth: 240,
                      }}
                    >
                      {p.isImage ? (
                        <PreviewImage
                          src={p.preview}
                          alt={p.file.name}
                          style={{ width: 75, height: 75, flex: "0 0 auto" }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 75,
                            height: 75,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PictureAsPdfIcon
                            sx={{ fontSize: 40, color: "#6C6C70" }}
                          />
                        </div>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.95rem",
                            wordBreak: "break-word",
                          }}
                        >
                          {p.file?.name && p.file?.name.startsWith("http")
                            ? p.originalName
                            : p.file.name}
                        </span>

                        <div style={{ marginTop: 6, display: "flex", gap: 12 }}>
                          {p.preview ? (
                            <>
                              <a
                                href={p.preview}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "var(--primary-color)",
                                  textDecoration: "none",
                                  fontSize: "0.875rem",
                                }}
                              >
                                ดู
                              </a>
                              {p.file?.name &&
                                !p.file?.name.startsWith("http") && (
                                  <a
                                    href={p.preview}
                                    download={p.file.name}
                                    style={{
                                      color: "var(--secondary-color)",
                                      textDecoration: "none",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    ดาวน์โหลด
                                  </a>
                                )}
                            </>
                          ) : (
                            <span
                              style={{
                                fontSize: "0.875rem",
                                color: "#6C6C70",
                              }}
                            >
                              ไม่สามารถดูตัวอย่าง
                            </span>
                          )}
                        </div>
                      </Box>

                      <IconButton
                        className="remove-image"
                        sx={{
                          p: "2px",
                          mr: 2,
                          backgroundColor: "var(--darkgrey-color)",
                          "&:hover": {
                            backgroundColor: "#6C6C70cc",
                          },
                        }}
                        onClick={() => removeCertificateFile(index)}
                      >
                        <ClearRoundedIcon
                          sx={{
                            fontSize: "16px",
                            color: "var(--white-color)",
                          }}
                        />
                      </IconButton>
                    </Box>
                  ))
                ) : (
                  <span>ไม่พบรายการเอกสารใบรับรอง</span>
                )}
              </UploadedPreview>
            </Grid>
          )}
        </Grid>

        {/* Sales Detail */}
        <Grid item xs={12} sx={{ "&.MuiGrid-item": { pt: 5 } }}>
          <SpanLabel>
            รายละเอียดการขาย<span>*</span>
          </SpanLabel>

          <Grid
            container
            spacing={3}
            rowSpacing={3}
            columnSpacing={3}
            sx={{
              mt: 0,
              ".MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-20px",
              },
            }}
          >
            <Grid item xs={6} sx={{ alignSelf: "center" }}>
              <WrapperFormLabel>
                <FormControlLabel
                  label="สินค้าพรีออเดอร์"
                  sx={{
                    margin: 0,
                    ".MuiFormControlLabel-label": {
                      fontSize: "1rem",
                    },
                    ".MuiCheckbox-root": {
                      p: 0,
                      pr: 1,
                    },
                  }}
                  control={
                    <Checkbox
                      name="pre-order-checkbox"
                      sx={{
                        "&.Mui-checked": {
                          color: "#e04132",
                        },
                        ".MuiSvgIcon-root": {
                          fontSize: "1.5rem",
                        },
                      }}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<RadioButtonCheckedIcon />}
                      checked={
                        checkNotEmpty(dataUploadChoice[0].pre_order_date)
                          ? true
                          : false
                      }
                      onChange={(e) => {
                        if (checkNotEmpty(dataUploadChoice[0].pre_order_date)) {
                          onChangeUpload(
                            null,
                            `choices.0.pre_order_date`,
                            null,
                          );
                        } else {
                          onChangeUpload(
                            null,
                            `choices.0.pre_order_date`,
                            "7 - 14  วัน",
                          );
                        }
                      }}
                    />
                  }
                />
              </WrapperFormLabel>
            </Grid>

            {checkNotEmpty(dataUploadChoice[0].pre_order_date) ? (
              <Grid item xs={3} sx={{ minHeight: 80 }}>
                <WrapperTextfield className="form-input">
                  <FormControl
                    fullWidth
                    error={!!choiceErrors[`choices[0].pre_order_date`]}
                  >
                    <InputLabel id="preorder-date-label">
                      จำนวนวันที่พร้อมส่ง
                    </InputLabel>
                    <Select
                      labelId="preorder-date-label"
                      id="preorder-date"
                      displayEmpty
                      defaultValue={"7 - 14  วัน"}
                      value={dataUploadChoice[0].pre_order_date}
                      label="จำนวนวันที่พร้อมส่ง"
                      onChange={(e) =>
                        onChangeUpload(e, `choices.0.pre_order_date`)
                      }
                    >
                      <MenuItem value={`7 - 14  วัน`}>7 - 14 วัน</MenuItem>
                      <MenuItem value={`15 - 30 วัน`}>15 - 30 วัน</MenuItem>
                      <MenuItem value={`35 - 60 วัน`}>35 - 60 วัน</MenuItem>
                    </Select>
                    {!!choiceErrors[`choices[0].pre_order_date`] && (
                      <FormHelperText sx={{ width: "max-content" }}>
                        Please select pre-order date
                      </FormHelperText>
                    )}
                  </FormControl>
                </WrapperTextfield>
              </Grid>
            ) : (
              <Grid item xs={3} sx={{ minHeight: 80 }}></Grid>
            )}

            <Grid item xs={6} sx={{ alignSelf: "center" }}>
              <WrapperFormLabel>
                <FormControlLabel
                  label="สินค้ามีสต็อก"
                  sx={{
                    margin: 0,
                    ".MuiFormControlLabel-label": {
                      fontSize: "1rem",
                    },
                    ".MuiCheckbox-root": {
                      p: 0,
                      pr: 1,
                    },
                  }}
                  control={
                    <Checkbox
                      name="instock-checkbox"
                      sx={{
                        "&.Mui-checked": {
                          color: "#e04132",
                        },
                        ".MuiSvgIcon-root": {
                          fontSize: "1.5rem",
                        },
                      }}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<RadioButtonCheckedIcon />}
                      checked={dataUploadChoice[0].is_instock}
                      onChange={(e) => {
                        onChangeUpload(
                          null,
                          `choices.0.is_instock`,
                          e.target.checked,
                        );

                        if (checkNotEmpty(dataUploadChoice[0].is_instock)) {
                          onChangeUpload(null, `choices.0.stock_count`, null);
                        } else {
                          onChangeUpload(null, `choices.0.stock_count`, 0);
                        }
                      }}
                    />
                  }
                />
              </WrapperFormLabel>
            </Grid>

            {dataUploadChoice[0].is_instock ? (
              <Grid item xs={3} sx={{ minHeight: 80 }}>
                <WrapperTextfield className="form-input">
                  <TextField
                    label="จำนวนสต็อกสินค้า"
                    variant="outlined"
                    fullWidth
                    value={dataUploadChoice[0]?.stock_count || ""}
                    onChange={(e) => {
                      if (e.target.value * 0 === 0)
                        onChangeUpload(
                          null,
                          `choices.0.stock_count`,
                          e.target.value,
                        );
                    }}
                    onBlur={(e) => {
                      if (e.target.value * 0 === 0)
                        onChangeUpload(
                          null,
                          `choices.0.stock_count`,
                          parseFloat(e.target.value) || null,
                        );
                    }}
                    error={!!choiceErrors[`choices[0].stock_count`]}
                    // helperText={
                    //   !!choiceErrors[`choices[0].pre_order_date`]
                    //     ? "โปรดระบุจจำนวนสต็อกสินค้า"
                    //     : ""
                    // }
                    FormHelperTextProps={{
                      style: { minHeight: "20px" },
                    }}
                  />
                </WrapperTextfield>
              </Grid>
            ) : null}

            {/* Unit Price */}
            <Grid item xs={12}>
              <UnitPriceGrid choiceIndex={0} />
            </Grid>
          </Grid>
        </Grid>

        {/* min order qty */}
        <Grid item xs={6}>
          <Grid container spacing={3} alignItems={"center"}>
            <Grid item xs={12}>
              <SpanLabel>จำนวนการสั่งซื้อขั้นต่ำ</SpanLabel>
            </Grid>

            <Grid item xs={6}>
              <WrapperTextfield className="form-input">
                <TextField
                  type="number"
                  label="จำนวน"
                  variant="outlined"
                  fullWidth
                  value={dataUpload?.min_order_qty}
                  onChange={(e) => {
                    onChangeUpload(e, `min_order_qty`);
                  }}
                  error={uploadErrors[`min_order_qty`]}
                  helperText={
                    uploadErrors[`min_order_qty`]
                      ? "โปรดระบุจำนวนการสั่งซื้อขั้นต่ำ"
                      : "   "
                  }
                  FormHelperTextProps={{
                    style: { minHeight: "20px" },
                  }}
                />
              </WrapperTextfield>
            </Grid>

            <Grid item xs={6} sx={{ pb: 3 }}>
              <SpanLabel>
                {dataUploadChoice[0]?.group_price?.unit_price?.unit_th
                  ? dataUploadChoice[0]?.group_price?.unit_price?.unit_th
                  : "หน่วย"}
              </SpanLabel>
            </Grid>
          </Grid>
        </Grid>

        {/* Sample & Installer detail / Hashtags */}
        <Grid item xs={12}>
          <SpanLabel>การบริการและตัวอย่างสินค้า</SpanLabel>

          <Grid
            container
            spacing={3}
            rowSpacing={3}
            columnSpacing={3}
            sx={{
              mt: 0,
              ".MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-20px",
              },
            }}
          >
            <Grid item xs={6}>
              <WrapperButton>
                <FormControlLabel
                  label="สินค้ามีช่างติดตั้ง"
                  sx={{
                    ".MuiFormControlLabel-label": {
                      ml: 1,
                      fontSize: font.size.md,
                    },
                  }}
                  control={
                    <Switch
                      sx={{ m: 1 }}
                      checked={dataUpload.is_installer}
                      onChange={(e) => {
                        onChangeUpload(null, `is_installer`, e.target.checked);
                      }}
                    />
                  }
                />
              </WrapperButton>

              {dataUpload?.is_installer && (
                <WrapperTextfield
                  className={
                    dataUploadChoice[0].group_price.install_price !== null
                      ? "form-input"
                      : ""
                  }
                  divPrice
                >
                  <TextField
                    label="ราคาช่าง/ตร.ม"
                    variant="outlined"
                    className="TextField-without-border-radius"
                    sx={{
                      width: "50%",
                      borderRadius: "16px",
                    }}
                    value={
                      dataUploadChoice[0]?.group_price?.install_price ?? ""
                    }
                    onChange={(e) => {
                      if (e.target.value * 0 === 0)
                        onChangeUpload(
                          null,
                          `is_installer.price`,
                          e.target.value,
                        );
                    }}
                    onBlur={(e) => {
                      if (e.target.value * 0 === 0)
                        onChangeUpload(
                          null,
                          `is_installer.price`,
                          parseFloat(e.target.value) || "",
                        );
                    }}
                    error={
                      !!choiceErrors[`choices[0].group_price.install_price`]
                    }
                    helperText={
                      !!choiceErrors[`choices[0].group_price.install_price`]
                        ? "โปรดระบุราคาติดตั้ง"
                        : ""
                    }
                    disabled={
                      dataUploadChoice[0]?.group_price?.install_price === 0 &&
                      !choiceErrors[`choices[0].group_price.install_price`]
                    }
                  />

                  <FormControlLabel
                    label="สินค้าติดตั้งฟรี"
                    sx={{
                      width: "50%",
                      margin: 0,
                    }}
                    control={
                      <Checkbox
                        name="install-price-checkbox"
                        sx={{
                          "&.Mui-checked": {
                            color: "#e04132",
                          },
                        }}
                        checked={
                          dataUploadChoice[0]?.group_price?.install_price === 0
                        }
                        onChange={(e) => {
                          onChangeUpload(
                            null,
                            `is_installer.is_free`,
                            e.target.checked,
                          );
                        }}
                      />
                    }
                  />
                </WrapperTextfield>
              )}
            </Grid>

            <Grid item xs={6}>
              <WrapperButton>
                <FormControlLabel
                  label="สินค้าสามารถขอแผ่นตัวอย่างได้"
                  sx={{
                    ".MuiFormControlLabel-label": {
                      ml: 1,
                      fontSize: font.size.md,
                    },
                  }}
                  control={
                    <Switch
                      sx={{ m: 1 }}
                      checked={dataUploadChoice[0]?.is_sample}
                      onChange={(e) => {
                        onChangeUpload(
                          null,
                          `choices.0.is_sample`,
                          e.target.checked,
                        );
                      }}
                    />
                  }
                />
              </WrapperButton>

              {dataUploadChoice[0]?.is_sample && (
                <WrapperTextfield
                  className={
                    dataUploadChoice[0].group_price.sample_price !== null
                      ? "form-input"
                      : ""
                  }
                  divPrice
                >
                  <TextField
                    label="ราคาสินค้าตัวอย่าง"
                    variant="outlined"
                    sx={{
                      width: "50%",
                      borderRadius: "16px",
                    }}
                    value={dataUploadChoice[0].group_price.sample_price ?? ""}
                    onChange={(e) => {
                      if (e.target.value * 0 === 0)
                        onChangeUpload(
                          null,
                          `choices.0.group_price.sample_price`,
                          e.target.value,
                        );
                    }}
                    onBlur={(e) => {
                      if (e.target.value * 0 === 0)
                        onChangeUpload(
                          null,
                          `choices.0.group_price.sample_price`,
                          parseFloat(e.target.value) || "",
                        );
                    }}
                    error={
                      !!choiceErrors[`choices[0].group_price.sample_price`]
                    }
                    helperText={
                      !!choiceErrors[`choices[0].group_price.sample_price`]
                        ? "โปรดระบุราคาสินค้าตัวอย่าง"
                        : ""
                    }
                    disabled={
                      dataUploadChoice[0]?.group_price?.sample_price === null &&
                      !choiceErrors[`choices[0].group_price.sample_price`]
                    }
                  />

                  <FormControlLabel
                    label="ขอสินค้าตัวอย่างฟรี"
                    sx={{
                      width: "50%",
                      maxHeight: "72px",
                      margin: 0,
                    }}
                    control={
                      <Checkbox
                        name="sample-price-checkbox"
                        sx={{
                          "&.Mui-checked": {
                            color: "#e04132",
                          },
                        }}
                        checked={
                          dataUploadChoice[0]?.group_price?.sample_price ===
                          null
                        }
                        onChange={(e) => {
                          onChangeUpload(
                            null,
                            `choices.0.group_price.sample_price.is_free`,
                            e.target.checked,
                          );
                        }}
                      />
                    }
                  />
                </WrapperTextfield>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <SpanLabel>แฮชแท็ก</SpanLabel>

                <AiGenerateButton />
              </Box>

              <WrapperTextfield className="form-input" divTool>
                <Autocomplete
                  id="combo-box-hashtag"
                  fullWidth
                  multiple
                  // options={masterdata?.hashtag_data?.map((tags) => ({ name: tags.name }))}
                  options={sortHashtag}
                  getOptionLabel={(option) => {
                    return `#${option.name}`;
                  }}
                  value={dataUploadChoice[0]?.hashtags}
                  isOptionEqualToValue={(option, value) =>
                    option?.name === value?.name
                  }
                  onChange={(e, value) => {
                    if (
                      dataUploadChoice[0]?.hashtags.length > 1 &&
                      value.length === 0
                    ) {
                      setConfirmInfo((info) => ({
                        ...info,
                        visible: true,
                        title:
                          getLocale() === "th"
                            ? `คุณต้องการลบแฮชแท็กทั้งหมดใช่หรือไม่`
                            : `คุณต้องการลบแฮชแท็กทั้งหมดใช่หรือไม่`,
                        confirmFunc: () => {
                          onChangeUpload(e, `choices.0.hashtags`, value);
                          setConfirmInfo(initialConfirm);
                        },
                      }));
                    } else {
                      onChangeUpload(e, `choices.0.hashtags`, value);
                    }
                  }}
                  freeSolo
                  disableCloseOnSelect
                  ListboxComponent={forwardRef(
                    ({ children, index, ...props }, ref) => (
                      <ul
                        {...props}
                        ref={ref}
                        key={`list-${index}`}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                        }}
                      >
                        {children}
                      </ul>
                    ),
                  )}
                  renderOption={(props, option, { selected }, index) => (
                    <>
                      {option?.empty ? (
                        <li
                          {...props}
                          key={`hashtag-option-${index}`}
                          style={{
                            maxWidth: "100%",
                            color: "var(--darkgrey-color)",
                          }}
                        >
                          {option?.empty}
                        </li>
                      ) : (
                        <li
                          {...props}
                          key={`hashtag-option-${index}`}
                          style={{
                            flex: "0 0 25%",
                            maxWidth: "25%",
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Checkbox
                              sx={{
                                "&.Mui-checked": {
                                  color: "#E13B30",
                                },
                              }}
                              checked={selected || false}
                            />
                            {option.name}
                          </div>
                          <p
                            style={{
                              width: "100%",
                              margin: 0,
                              fontSize: "0.75rem",
                              color: "#6C6C70",
                              paddingLeft: "40px",
                            }}
                          >
                            ถูกใช้ไปแล้ว {option.product_count || 0} ครั้ง
                          </p>
                        </li>
                      )}
                    </>
                  )}
                  noOptionsText="Press ENTER to add new hashtag"
                  // filterOptions={filterOptions}
                  filterOptions={(options, params) => {
                    const filtered = filterOptions(options, params);

                    if (filtered.length === 0 && params.inputValue !== "") {
                      filtered.push({
                        inputValue: params.inputValue,
                        empty: `Press ENTER to add new hashtag`,
                      });
                    }

                    return filtered;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="#Hashtag"
                      error={!!choiceErrors[`choices[0].hashtags`]}
                      helperText={
                        !!choiceErrors[`choices[0].hashtags`] ? (
                          "กรุณาเลือกแฮชแท็ก"
                        ) : (
                          <Box sx={{ height: "20px" }} />
                        )
                      }
                    />
                  )}
                />
                <Tooltip
                  title={
                    "การใส่แฮชแท็กจะช่วยให้ลูกค้าค้นหาสินค้าของคุณเจอได้ง่ายยิ่งขึ้น"
                  }
                />
              </WrapperTextfield>
            </Grid>
          </Grid>
        </Grid>

        {/* Description Product */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2}>
            <SpanLabel>รายละเอียดสินค้า</SpanLabel>

            <AiGenerateButton />
          </Box>

          <WrapperTextfield className="form-input" textEditor>
            <TextEditor
              name={`description`}
              value={dataUpload.description}
              onChange={(data) => {
                onChangeUpload(null, `description`, data);
              }}
              editorLoaded={editorLoaded}
              customConfig={{
                toolbar: [
                  "undo",
                  "redo",
                  "heading",
                  "|",
                  "bold",
                  "bulletedList",
                  "numberedList",
                ],
                heading: {
                  options: [
                    {
                      model: "heading3",
                      view: "h3",
                      title: "Heading",
                      class: "ck-heading_heading3",
                    },
                    {
                      model: "paragraph",
                      title: "Body",
                      class: "ck-heading_paragraph",
                    },
                  ],
                },
              }}
            />
          </WrapperTextfield>
        </Grid>

        {/* Similar Product in seller shop*/}
        <Grid item xs={12}>
          <SpanLabel>สินค้าใกล้เคียงกันในร้าน</SpanLabel>

          <WarningText sx={{ mt: "14px" }}>
            <ErrorIcon sx={{ color: "var(--gold-color)" }} />

            <span>
              สินค้ารายการที่คุณเลือก จะถูกแสดงผลอยู่ในสินค้าที่คุณกำลังอัพโหลด
            </span>
          </WarningText>

          <Grid
            container
            rowSpacing={2}
            columnSpacing={0}
            sx={{
              mt: 0,
              ".MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-20px",
              },
            }}
          >
            <Grid item xs={12}>
              <WrapperTextfield className="form-input" divTool>
                <Autocomplete
                  multiple
                  id="combo-box-similars"
                  fullWidth
                  options={similarData?.similarAllSeller}
                  isOptionEqualToValue={(option, value) =>
                    option?.similar?._id === value?.similar?._id
                  }
                  // getOptionSelected={(option, value) => option._id === value._id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      {!!option?.similar?.group_image?.product_display_image ===
                      false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <img
                          style={{
                            width: "75px",
                            height: "75px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "16px",
                            marginRight: "8px",
                          }}
                          src={
                            option?.similar?.group_image?.product_display_image
                              ? option?.similar?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={
                            option?.similar?.group_image?.product_display_image
                          }
                        />
                      )}
                      <div style={{ display: "grid" }}>
                        <span>
                          {option?.similar?.product?.item_name
                            ? option?.similar?.product?.item_name +
                              ` - ( ${option.similar?.name} )`
                            : "ไม่มีชื่อ"}
                        </span>
                        <span>
                          {option?.similar?.product?.item_code
                            ? option?.similar?.product?.item_code
                            : "-"}
                        </span>
                        <span>
                          {option?.similar?.group_price?.is_start_price === true
                            ? option?.similar?.group_price?.start_price
                            : option?.similar?.group_price?.price}
                          {getLocale() === "th"
                            ? ` บาท / ` +
                              option?.similar?.group_price?.unit_price?.unit_th
                            : ` baht / ` +
                              option?.similar?.group_price?.unit_price?.unit_en}
                        </span>
                      </div>
                    </li>
                  )}
                  value={dataSimilar?.similar_seller}
                  onChange={(event, newValue) => {
                    setDataSimilar({
                      ...dataSimilar,
                      similar_seller: newValue,
                    });
                  }}
                  getOptionLabel={(option) => {
                    return (
                      option?.similar?.product?.item_name +
                      " - " +
                      option?.similar?.product?.item_code +
                      (option?.similar?.name !== ""
                        ? ` - (` + option?.similar?.name + `)`
                        : ``)
                    );
                  }}
                  filterOptions={filterOptions}
                  limitTags={2}
                  renderInput={(params) => (
                    <TextField
                      className="TextField-without-border-radius"
                      {...params}
                      label="สินค้าใกล้เคียงกันในร้าน"
                    />
                  )}
                />
                <Tooltip
                  title={
                    " คุณสามารถเพิ่มสินค้าที่มีสี, ลวดลาย, หรือประเภท ที่ใกล้เคียงกับสินค้าตัวนี้ เพื่อเพิ่มโอกาสในการเสนอขายมากยิ่งขึ้น"
                  }
                />
              </WrapperTextfield>
            </Grid>

            {dataSimilar?.similar_seller?.length > 0 && (
              <Grid item xs={12}>
                <RowProductCard>
                  {dataSimilar?.similar_seller?.map(
                    ({ similar: product }, index) => (
                      <WrapperProductCard
                        key={`product-${product?._id}-${index}`}
                      >
                        <ProductCard
                          id={product?._id}
                          sellerLogo={
                            product?.product?.user?.seller?.profile_image
                          }
                          sellerName={
                            product?.product?.user?.seller?.company_name
                          }
                          src={product?.group_image?.product_full_image.replace(
                            ".com",
                            ".com/200x200",
                          )}
                          itemcode={product?.product?.item_code}
                          itemname={product?.product?.item_name}
                          price={product?.group_price}
                          productType={product?.product?.product_types
                            .map((data) =>
                              getLocale() === "th"
                                ? data.type_th
                                : data.type_en,
                            )
                            .join(",")}
                          view={product?.view_count}
                          size={product?.size}
                          discount={
                            product?.reduce_percent &&
                            typeof product?.reduce_percent === "number"
                              ? product?.reduce_percent?.toFixed(0)
                              : null
                          }
                        />
                      </WrapperProductCard>
                    ),
                  )}
                </RowProductCard>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Similar Product in platform (Admin) */}
        {user?.role_id === 1 && (
          <Grid item xs={12}>
            <SpanLabel>สินค้าใกล้เคียงกันในแพลตฟอร์ม</SpanLabel>

            <WarningText sx={{ mt: "14px" }}>
              <ErrorIcon sx={{ color: "var(--gold-color)" }} />

              <span>
                สินค้ารายการที่คุณเลือก
                จะถูกแสดงผลอยู่ในสินค้าที่คุณกำลังอัพโหลด
              </span>
            </WarningText>

            <Grid
              container
              rowSpacing={2}
              columnSpacing={0}
              sx={{
                mt: 0,
                ".MuiFormHelperText-root": {
                  position: "absolute",
                  bottom: "-20px",
                },
              }}
            >
              <Grid item xs={12}>
                <WrapperTextfield className="form-input" divTool>
                  <Autocomplete
                    multiple
                    id="combo-box-admin-similars"
                    fullWidth
                    options={similarData?.similarAllAdmin}
                    isOptionEqualToValue={(option, value) =>
                      option?.similar?._id === value?.similar?._id
                    }
                    // getOptionSelected={(option, value) => option._id === value._id}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        {!!option?.similar?.group_image
                          ?.product_display_image === false ? (
                          <ImageEmpty>
                            <InsertPhotoIcon
                              sx={{
                                color: "rgba(0, 0, 0, 0.54)",
                                cursor: "pointer",
                              }}
                            />
                          </ImageEmpty>
                        ) : (
                          <img
                            style={{
                              width: "75px",
                              height: "75px",
                              objectFit: "cover",
                              objectPosition: "center",
                              borderRadius: "16px",
                              marginRight: "8px",
                            }}
                            src={
                              option?.similar?.group_image
                                ?.product_display_image
                                ? option?.similar?.group_image?.product_display_image.replace(
                                    ".com",
                                    ".com/300x300",
                                  )
                                : ""
                            }
                            alt={
                              option?.similar?.group_image
                                ?.product_display_image
                            }
                          />
                        )}
                        <div style={{ display: "grid" }}>
                          <span>
                            {option?.similar?.product?.item_name
                              ? option?.similar?.product?.item_name +
                                ` - ( ${option.similar?.name} )`
                              : "ไม่มีชื่อ"}
                          </span>
                          <span>
                            {option?.similar?.product?.item_code
                              ? option?.similar?.product?.item_code
                              : "-"}
                          </span>
                          <span>
                            {option?.similar?.group_price?.is_start_price ===
                            true
                              ? option?.similar?.group_price?.start_price
                              : option?.similar?.group_price?.price}
                            {getLocale() === "th"
                              ? ` บาท / ` +
                                option?.similar?.group_price?.unit_price
                                  ?.unit_th
                              : ` baht / ` +
                                option?.similar?.group_price?.unit_price
                                  ?.unit_en}
                          </span>
                        </div>
                      </li>
                    )}
                    value={dataSimilar?.similar_admin}
                    onChange={(event, newValue) => {
                      setDataSimilar({
                        ...dataSimilar,
                        similar_admin: newValue,
                      });
                    }}
                    getOptionLabel={(option) => {
                      return (
                        option?.similar?.product?.item_name +
                        " - " +
                        option?.similar?.product?.item_code +
                        (option?.similar?.name !== ""
                          ? ` - (` + option?.similar?.name + `)`
                          : ``)
                      );
                    }}
                    filterOptions={filterOptions}
                    limitTags={2}
                    renderInput={(params) => (
                      <TextField
                        className="TextField-without-border-radius"
                        {...params}
                        label="สินค้าใกล้เคียงกันในแพลตฟอร์ม"
                      />
                    )}
                  />
                  <Tooltip
                    title={
                      " คุณสามารถเพิ่มสินค้าที่มีสี, ลวดลาย, หรือประเภท ที่ใกล้เคียงกับสินค้าตัวนี้ เพื่อเพิ่มโอกาสในการเสนอขายมากยิ่งขึ้น"
                    }
                  />
                </WrapperTextfield>
              </Grid>

              {dataSimilar?.similar_admin?.length > 0 && (
                <Grid item xs={12}>
                  <RowProductCard>
                    {dataSimilar?.similar_admin?.map(
                      ({ similar: product }, index) => (
                        <WrapperProductCard
                          key={`product-${product?._id}-${index}`}
                        >
                          <ProductCard
                            id={product?._id}
                            sellerLogo={
                              product?.product?.user?.seller?.profile_image
                            }
                            sellerName={
                              product?.product?.user?.seller?.company_name
                            }
                            src={product?.group_image?.product_full_image.replace(
                              ".com",
                              ".com/200x200",
                            )}
                            itemcode={product?.product?.item_code}
                            itemname={product?.product?.item_name}
                            price={product?.group_price}
                            productType={product?.product?.product_types
                              .map((data) =>
                                getLocale() === "th"
                                  ? data.type_th
                                  : data.type_en,
                              )
                              .join(",")}
                            view={product?.view_count}
                            size={product?.size}
                            discount={
                              product?.reduce_percent &&
                              typeof product?.reduce_percent === "number"
                                ? product?.reduce_percent?.toFixed(0)
                                : null
                            }
                          />
                        </WrapperProductCard>
                      ),
                    )}
                  </RowProductCard>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}

        {/* similar product to other sellers */}

        <Grid item xs={12}>
          <SpanLabel>สินค้าใกล้เคียงกันจากร้านค้าอื่น</SpanLabel>
          <WarningText sx={{ mt: "14px" }}>
            <ErrorIcon sx={{ color: "var(--gold-color)" }} />

            <span>
              สินค้ารายการนี้ที่คุณอัพโหลด
              จะถูกแสดงผลอยู่ในรายการสินค้าที่คุณเลือกด้านล่าง
            </span>
          </WarningText>

          <Grid
            container
            rowSpacing={2}
            columnSpacing={0}
            sx={{
              mt: 0,
              ".MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-20px",
              },
            }}
          >
            <Grid item xs={12}>
              <WrapperTextfield className="form-input" divTool>
                <Autocomplete
                  multiple
                  id="combo-box-admin-similars"
                  fullWidth
                  options={similarData?.similarAllAdmin}
                  isOptionEqualToValue={(option, value) =>
                    option?.similar?._id === value?._id
                  }
                  // getOptionSelected={(option, value) => option._id === value._id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      {!!option?.similar?.group_image?.product_display_image ===
                      false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <img
                          style={{
                            width: "75px",
                            height: "75px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "16px",
                            marginRight: "8px",
                          }}
                          src={
                            option?.similar?.group_image?.product_display_image
                              ? option?.similar?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={
                            option?.similar?.group_image?.product_display_image
                          }
                        />
                      )}
                      <div style={{ display: "grid" }}>
                        <span>
                          {option?.similar?.product?.item_name
                            ? option?.similar?.product?.item_name +
                              ` - ( ${option.similar?.name} )`
                            : "ไม่มีชื่อ"}
                        </span>
                        <span>
                          {option?.similar?.product?.item_code
                            ? option?.similar?.product?.item_code
                            : "-"}
                        </span>
                        <span>
                          {option?.similar?.group_price?.is_start_price === true
                            ? option?.similar?.group_price?.start_price
                            : option?.similar?.group_price?.price}
                          {getLocale() === "th"
                            ? ` บาท / ` +
                              option?.similar?.group_price?.unit_price?.unit_th
                            : ` baht / ` +
                              option?.similar?.group_price?.unit_price?.unit_en}
                        </span>
                      </div>
                    </li>
                  )}
                  value={dataSimilar?.similar_product_to_other_seller}
                  onChange={(event, newValue) => {
                    setDataSimilar({
                      ...dataSimilar,
                      similar_product_to_other_seller: newValue,
                    });
                  }}
                  getOptionLabel={(option) => {
                    return (
                      option?.similar?.product?.item_name +
                      " - " +
                      option?.similar?.product?.item_code +
                      (option?.similar?.name !== ""
                        ? ` - (` + option?.similar?.name + `)`
                        : ``)
                    );
                  }}
                  filterOptions={filterOptions}
                  limitTags={2}
                  renderInput={(params) => (
                    <TextField
                      className="TextField-without-border-radius"
                      {...params}
                      label="สินค้าใกล้เคียงกันจากร้านค้าอื่น"
                    />
                  )}
                />
                <Tooltip
                  title={
                    "สินค้ารายการนี้ที่คุณอัพโหลดจะถูกแสดงผลอยู่ในรายการสินค้าที่คุณเลือกด้านล่าง"
                  }
                />
              </WrapperTextfield>
            </Grid>

            {dataSimilar?.similar_product_to_other_seller?.length > 0 && (
              <Grid item xs={12}>
                <RowProductCard>
                  {dataSimilar?.similar_product_to_other_seller?.map(
                    ({ similar: product }, index) => (
                      <WrapperProductCard
                        key={`product-${product?._id}-${index}`}
                      >
                        <ProductCard
                          id={product?._id}
                          sellerLogo={
                            product?.product?.user?.seller?.profile_image
                          }
                          sellerName={
                            product?.product?.user?.seller?.company_name
                          }
                          src={product?.group_image?.product_full_image.replace(
                            ".com",
                            ".com/200x200",
                          )}
                          itemcode={product?.product?.item_code}
                          itemname={product?.product?.item_name}
                          price={product?.group_price}
                          productType={product?.product?.product_types
                            .map((data) =>
                              getLocale() === "th"
                                ? data.type_th
                                : data.type_en,
                            )
                            .join(",")}
                          view={product?.view_count}
                          size={product?.size}
                          discount={
                            product?.reduce_percent &&
                            typeof product?.reduce_percent === "number"
                              ? product?.reduce_percent?.toFixed(0)
                              : null
                          }
                        />
                      </WrapperProductCard>
                    ),
                  )}
                </RowProductCard>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <SpanLabel>สินค้าที่ใช้คู่กัน</SpanLabel>
          {/* <WarningText sx={{ mt: "14px" }}>
            <ErrorIcon sx={{ color: "var(--gold-color)" }} />

            <span>
              สินค้าที่คุณกำลังอัพโหลด จะถูกแสดงอยู่ในรายการสินค้าที่คุณเลือก
            </span>
          </WarningText> */}

          <Grid
            container
            rowSpacing={2}
            columnSpacing={0}
            sx={{
              mt: 0,
              ".MuiFormHelperText-root": {
                position: "absolute",
                bottom: "-20px",
              },
            }}
          >
            <Grid item xs={12}>
              <WrapperTextfield className="form-input" divTool>
                <Autocomplete
                  multiple
                  id="combo-box-bundles"
                  fullWidth
                  options={productList.map((product) => ({
                    bundle: product,
                  }))}
                  isOptionEqualToValue={(option, value) =>
                    option?.bundle?._id === value?.bundle?._id
                  }
                  // getOptionSelected={(option, value) => option._id === value._id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      {!!option?.bundle?.group_image?.product_display_image ===
                      false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <img
                          style={{
                            width: "75px",
                            height: "75px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "16px",
                            marginRight: "8px",
                          }}
                          src={
                            option?.bundle?.group_image?.product_display_image
                              ? option?.bundle?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={
                            option?.bundle?.group_image?.product_display_image
                          }
                        />
                      )}
                      <div style={{ display: "grid" }}>
                        <span>
                          {option?.bundle?.product?.item_name
                            ? option?.bundle?.product?.item_name +
                              ` - ( ${option?.bundle?.name} )`
                            : "ไม่มีชื่อ"}
                        </span>
                        <span>
                          {option?.bundle?.product?.item_code
                            ? option?.bundle?.product?.item_code
                            : "-"}
                        </span>
                        <span>
                          {option?.bundle?.group_price?.is_start_price === true
                            ? option?.bundle?.group_price?.start_price
                            : option?.bundle?.group_price?.price}
                          {getLocale() === "th"
                            ? ` บาท / ` +
                              option?.bundle?.group_price?.unit_price?.unit_th
                            : ` baht / ` +
                              option?.bundle?.group_price?.unit_price?.unit_en}
                        </span>
                      </div>
                    </li>
                  )}
                  value={dataUpload?.bundles}
                  onChange={(event, newValue) => {
                    if (newValue.length > 10) {
                      enqueueSnackbar(
                        `สามารถเลือกสินค้าที่ใช้คู่กันได้สูงสุด 10 รายการ`,
                        {
                          variant: "error",
                        },
                      );
                      return;
                    }
                    onChangeUpload(null, `bundles`, newValue);
                  }}
                  getOptionLabel={(option) => {
                    return (
                      option?.bundle?.product?.item_name +
                      " - " +
                      option?.bundle?.product?.item_code +
                      (option?.bundle?.name !== ""
                        ? ` - (` + option?.bundle?.name + `)`
                        : ``)
                    );
                  }}
                  filterOptions={filterOptions}
                  limitTags={2}
                  renderInput={(params) => (
                    <TextField
                      className="TextField-without-border-radius"
                      {...params}
                      label="สินค้าที่ใช้คู่กัน"
                    />
                  )}
                />
                <Tooltip
                  title={
                    "คุณสามารถเลือกสินค้าที่ใช้คู่กันกับสินค้านี้ เพื่อเพิ่มโอกาสในการเสนอขายมากยิ่งขึ้น"
                  }
                />
              </WrapperTextfield>
            </Grid>

            {dataUpload.bundles.length > 0 && (
              <Grid item xs={12}>
                <RowProductCard>
                  {dataUpload.bundles.map((product, index) => (
                    <WrapperProductCard
                      key={`product-${product?.bundle?._id}-${index}`}
                    >
                      <ProductCard
                        id={product?.bundle?._id}
                        sellerLogo={
                          product?.bundle?.product?.user?.seller?.profile_image
                        }
                        sellerName={
                          product?.bundle?.product?.user?.seller?.company_name
                        }
                        src={product?.bundle?.group_image?.product_full_image.replace(
                          ".com",
                          ".com/200x200",
                        )}
                        itemcode={product?.bundle?.product?.item_code}
                        itemname={product?.bundle?.product?.item_name}
                        price={product?.bundle?.group_price}
                        productType={product?.bundle?.product?.product_types
                          .map((data) =>
                            getLocale() === "th" ? data.type_th : data.type_en,
                          )
                          .join(",")}
                        view={product?.bundle?.view_count}
                        size={product?.bundle?.size}
                        discount={
                          product?.bundle?.reduce_percent &&
                          typeof product?.bundle?.reduce_percent === "number"
                            ? product?.bundle?.reduce_percent?.toFixed(0)
                            : null
                        }
                      />
                    </WrapperProductCard>
                  ))}
                </RowProductCard>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default MainProduct;
